import React, { useState } from 'react';
import QuickStartSidebar from "../components/quickstart/QuickStartSidebar";
import QuickStartContent from "../components/quickstart/QuickStartContent";

const QuickStart = () => {
    const [activeStep, setActiveStep] = useState('overview');

    const quickStartSteps = [
        {
            id: 'overview',
            title: 'Aperçu Rapide',
            icon: '🚀',
            duration: '2 min',
            difficulty: 'Facile'
        },
        {
            id: 'prerequisites',
            title: 'Prérequis',
            icon: '📋',
            duration: '3 min',
            difficulty: 'Facile'
        },
        {
            id: 'authentication',
            title: 'Authentification',
            icon: '🔐',
            duration: '5 min',
            difficulty: 'Moyen'
        },
        {
            id: 'first-payment',
            title: 'Premier Paiement',
            icon: '💳',
            duration: '10 min',
            difficulty: 'Moyen'
        },
        {
            id: 'webhooks',
            title: 'Configuration Webhooks',
            icon: '🔔',
            duration: '8 min',
            difficulty: 'Intermédiaire'
        },
        {
            id: 'deployment',
            title: 'Mise en Production',
            icon: '⚡',
            duration: '7 min',
            difficulty: 'Avancé'
        }
    ];

    const activeStepData = quickStartSteps.find(step => step.id === activeStep);

    return (
        <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50 pt-16 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950 transition-colors">
            {/* Header Hero */}
            <div className="bg-gradient-to-r from-amber-600 to-orange-600 text-white py-20">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center">
                        <div className="inline-flex items-center px-6 py-3 rounded-full bg-white/10 backdrop-blur-md border border-white/20 mb-6">
                            <span className="w-3 h-3 bg-emerald-400 rounded-full mr-3 animate-pulse"></span>
                            <span className="text-white/90 font-semibold text-lg">DÉMARRAGE RAPIDE</span>
                        </div>
                        <h1 className="text-4xl lg:text-5xl font-bold mb-4">
                            Première intégration en{' '}
                            <span className="bg-gradient-to-r from-amber-200 to-yellow-200 bg-clip-text text-transparent">
                                moins de 15 minutes
                            </span>
                        </h1>
                        <p className="text-xl text-white/80 max-w-3xl mx-auto leading-relaxed">
                            Suivez ce guide étape par étape pour implémenter votre premier paiement Netikash
                            rapidement et efficacement.
                        </p>

                        {/* Quick Stats */}
                        <div className="grid grid-cols-3 gap-8 mt-12 max-w-2xl mx-auto">
                            <div className="text-center">
                                <div className="text-3xl font-bold mb-1">5 min</div>
                                <div className="text-amber-200 text-sm">Premier paiement</div>
                            </div>
                            <div className="text-center">
                                <div className="text-3xl font-bold mb-1">3 étapes</div>
                                <div className="text-amber-200 text-sm">Simples et claires</div>
                            </div>
                            <div className="text-center">
                                <div className="text-3xl font-bold mb-1">100%</div>
                                <div className="text-amber-200 text-sm">Fonctionnel</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Main Content */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
                <div className="flex flex-col lg:flex-row gap-8">
                    {/* Sidebar Navigation */}
                    <div className="lg:w-80 flex-shrink-0">
                        <QuickStartSidebar
                            steps={quickStartSteps}
                            activeStep={activeStep}
                            onStepChange={setActiveStep}
                        />
                    </div>

                    {/* Main Content */}
                    <div className="flex-1 min-w-0">
                        <div className="bg-white rounded-2xl shadow-lg border border-amber-100/50 overflow-hidden">
                            <QuickStartContent step={activeStepData} />
                        </div>

                        {/* Navigation Buttons */}
                        <div className="flex justify-between items-center mt-8">
                            <button
                                onClick={() => {
                                    const currentIndex = quickStartSteps.findIndex(step => step.id === activeStep);
                                    if (currentIndex > 0) {
                                        setActiveStep(quickStartSteps[currentIndex - 1].id);
                                    }
                                }}
                                className="flex items-center space-x-2 px-6 py-3 rounded-lg border border-amber-300 text-amber-700 font-medium hover:bg-amber-50 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                                disabled={activeStep === 'overview'}
                            >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                                </svg>
                                <span>Étape précédente</span>
                            </button>

                            <button
                                onClick={() => {
                                    const currentIndex = quickStartSteps.findIndex(step => step.id === activeStep);
                                    if (currentIndex < quickStartSteps.length - 1) {
                                        setActiveStep(quickStartSteps[currentIndex + 1].id);
                                    }
                                }}
                                className="flex items-center space-x-2 px-6 py-3 rounded-lg bg-gradient-to-r from-amber-500 to-yellow-600 text-white font-medium hover:from-amber-600 hover:to-yellow-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                                disabled={activeStep === 'deployment'}
                            >
                                <span>{activeStep === 'deployment' ? 'Terminer' : 'Étape suivante'}</span>
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                </svg>
                            </button>
                        </div>

                        {/* Quick Actions */}
                        {activeStep === 'deployment' && (
                            <div className="grid md:grid-cols-2 gap-6 mt-8">
                                <div className="bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-2xl p-6 text-center">
                                    <h4 className="font-bold text-lg mb-2">🎉 Félicitations !</h4>
                                    <p className="text-white/90 mb-4">Vous avez terminé l'intégration</p>
                                    <button className="bg-white text-green-600 px-6 py-2 rounded-lg font-semibold hover:bg-gray-100 transition-colors">
                                        Voir la documentation complète
                                    </button>
                                </div>
                                <div className="bg-gradient-to-r from-blue-500 to-cyan-500 text-white rounded-2xl p-6 text-center">
                                    <h4 className="font-bold text-lg mb-2">🧪 Testez maintenant</h4>
                                    <p className="text-white/90 mb-4">Essayez votre intégration</p>
                                    <button className="bg-white text-blue-600 px-6 py-2 rounded-lg font-semibold hover:bg-gray-100 transition-colors">
                                        Accéder au Sandbox
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default QuickStart;